<template>
  <div class="flex gap-24 mt-40 w-[63.2rem]">
    <launch-dlc-content-setting
      v-if="!isShowTabList && isMounted"
      :key="forceRenderKey"
      :typeGame="typeGame"
      :installationPathAndPcClientSettings="installationPathAndPcClientSettings"
      :gameId="gameId"
      :releaseType="releaseType"
      :planStatus="planStatus"
      :verifyStatus="verifyStatus"
    />

    <s-tabs
      v-else
      v-model="selectedTab"
      defaultValue="'tab02'"
      variant="line-fixed"
      size="lg"
      :defaultIndex="0"
      class="relative stove-studio-tab-line larger"
    >
      <product-guide class="!max-w-[39rem] w-[39rem] mb-[-.4rem]" topStart :storedName="`${IS_CLOSE_LAUNCH_SETTING_GUIDE}_${productNo}`">
        <s-text as="h4" role="text4" class="font-bold">
          <safe-html :html="$t('studio.execution_setting.test_package_tab_tooltip_title')" />
        </s-text>
        <s-text as="p" role="cap1">
          <safe-html :html="$t('studio.execution_setting.test_package_tab_tooltip_msg')" />
        </s-text>
      </product-guide>
      <s-tab-list class="bg-transparent w-max max-w-full">
        <s-tab-item :value="RUN_OPTION_STATUS.TEST" class="min-w-136 min-h-48 !basis-[unset]">
          <span class="px-12 py-[.9rem] text-xl leading-xl text-center flex items-center gap-4">
            {{ $t('studio.execution_setting.test_tab') }}
          </span>
        </s-tab-item>
        <s-tab-item v-if="hasValidComingSoonSettings" :value="RUN_OPTION_STATUS.COMING_SOON" class="min-w-136 min-h-48 !basis-[unset]">
          <span class="px-12 py-[.9rem] text-xl leading-xl text-center flex items-center gap-4">
            {{ hasLiveSettings ? $t('studio.execution_setting.scheduled_tab') : $t('studio.execution_setting.live_tab') }}
          </span>
        </s-tab-item>
        <s-tab-item v-if="hasLiveSettings" :value="RUN_OPTION_STATUS.LIVE" class="min-w-136 min-h-48 !basis-[unset]">
          <span class="px-12 py-[.9rem] text-xl leading-xl text-center flex items-center gap-4">
            {{ $t('studio.execution_setting.live_tab') }}
          </span>
        </s-tab-item>
      </s-tab-list>

      <s-tab-panels>
        <s-tab-panel :value="RUN_OPTION_STATUS.TEST" :class="'mt-24'">
          <st-box class="!rounded-[2.8rem] !py-24 mt-24 mb-16">
            <h3 class="text-on-surface-elevation-2">
              <safe-html :html="$t('studio.execution_setting.test_package_guide1')" />
            </h3>
            <p class="text-on-surface-elevation-3 mt-4">
              <safe-html :html="$t('studio.execution_setting.test_package_guide2')" />
            </p>
            <s-button
              variant="outline"
              icon="ic-v2-control-add-line"
              size="sm"
              class="w-fit mt-16"
              @click="onLoadLiveSettings"
            >
              {{ $t('studio.execution_setting.fetch_live_package_data_btn') }}
            </s-button>
          </st-box>

          <launch-dlc-content-setting
            :key="`test-tab-${forceRenderKey}`"
            :typeGame="typeGame"
            :installationPathAndPcClientSettings="testTabSettings"
            :gameId="gameId"
            :releaseType="releaseType"
            :planStatus="planStatus"
            :verifyStatus="verifyStatus"
            :tabName="RUN_OPTION_STATUS.TEST"
            :isCallClearRunOptionsApi="isCallClearRunOptionsApi"
            @resetClearRunOptionsApi="setIsCallClearRunOptionsApi(false)"
          />
        </s-tab-panel>

        <s-tab-panel v-if="hasValidComingSoonSettings" :value="RUN_OPTION_STATUS.COMING_SOON" class="mt-24">
          <div class="flex flex-col text-center gap-4 items-center !rounded-[2.8rem] !py-20 !px-[1rem] mt-24 bg-brand-primary">
            <s-text as="h3" role="title6" class="flex items-center gap-4 text-white !font-bold">
              <s-icon icon="ic-v2-state-info-circle-line" size="4xl" class="mt-[-.2rem]" />
              {{ getDateTimeByLocale(comingSoonDetails?.buildSchedule.scheduledStartedAt!, true) }}
            </s-text>
            <s-text as="p" role="text1" class="text-white font-medium">{{ $t('studio.execution_setting.scheduled_info_guide') }}</s-text>
          </div>
          <st-box class="flex flex-col gap-24 !rounded-[4rem] !py-[3rem] my-24">
            <s-text as="h3" role="text1" class="text-on-surface-elevation-2 font-bold">
              {{ $t('studio.execution_setting.build_info') }}
            </s-text>
            <div class="flex flex-col gap-4">
              <s-text as="p" role="text3" class="text-on-surface-elevation-2 font-bold">
                {{ comingSoonDetails?.buildNo }}
              </s-text>
              <s-text as="p" role="btn2" class="text-on-surface-elevation-3">
                {{ comingSoonDetails?.buildDescription }}
              </s-text>
            </div>
          </st-box>
          <launch-dlc-content-setting
            :key="`coming-soon-tab-${forceRenderKey}`"
            :typeGame="typeGame"
            :installationPathAndPcClientSettings="comingSoonTabSettings"
            :gameId="gameId"
            :releaseType="releaseType"
            :planStatus="planStatus"
            :verifyStatus="verifyStatus"
            :tabName="RUN_OPTION_STATUS.COMING_SOON"
          />
          <div class="flex justify-center">
            <s-button
              variant="primary"
              size="lg"
              class="w-fit mt-16"
              @click="onCancelComingSoon"
            >
              {{ $t('studio.execution_setting.scheduled_pack_cancel_btn') }}
            </s-button>
          </div>
        </s-tab-panel>

        <s-tab-panel :value="RUN_OPTION_STATUS.LIVE" class="mt-24">
          <st-box class="flex flex-col gap-24 !rounded-[4rem] !py-[3rem] my-24">
            <s-text as="h3" role="text1" class="text-on-surface-elevation-2 font-bold">
              {{ $t('studio.execution_setting.build_info') }}
            </s-text>
            <div class="flex flex-col gap-4">
              <s-text as="p" role="text3" class="text-on-surface-elevation-2 font-bold">
                {{ liveDetails?.buildNo }}
              </s-text>
              <s-text as="p" role="btn2" class="text-on-surface-elevation-3">
                {{ liveDetails?.buildDescription }}
              </s-text>
              <s-text v-if="liveDetails?.releaseStatus === RELEASE_STATUS_BUILD.COMING_SOON && dateSettings" as="p" role="text3" class="text-on-surface-elevation-3">
                {{ dateSettings }}
                <s-text as="span" role="text3" class="text-brand-primary">{{ $t('studio.execution_setting.live_apply_popup_title') }}</s-text>
              </s-text>
            </div>
          </st-box>
          <launch-dlc-content-setting
            :key="`live-tab-${forceRenderKey}`"
            :typeGame="typeGame"
            :installationPathAndPcClientSettings="liveTabSettings"
            :gameId="gameId"
            :releaseType="releaseType"
            :planStatus="planStatus"
            :verifyStatus="verifyStatus"
            :tabName="RUN_OPTION_STATUS.LIVE"
          />
        </s-tab-panel>
      </s-tab-panels>
    </s-tabs>
  </div>
</template>
<script lang="ts" setup>
import { computed, nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import { deleteBuildScheduleApi, fetchBuildsPlanDetailApi, fetchInstallPathAndPcClientSettingsOnTabApi } from '@/apis/launch-settings.api';
import safeHtml from '@/components/common/safe-html.vue';
import StBox from '@/components/common/st-box.vue';
import LaunchDlcContentSetting from '@/components/launch-setting/tabs/launch-dlc-content-setting.vue';
import ProductGuide from '@/components/product/product-guide.vue';
import { showConfirm } from '@/composables/useDialog';
import { RELEASE_STATUS_BUILD } from '@/constants/build.const';
import { RUN_OPTION_STATUS, type RunOptionStatusType } from '@/constants/launch-setting.const';
import type { BuildsPlanDetailResponse, InstallPathAndPcClientSettingsResponse } from '@/types/launch-settings/launch-settings-response.type';
import { getDateTimeByLocale } from '@/utils/date.util';
import { isProductInReleaseOrReviewStatus } from '@/utils/launch-settings.util';

interface ExtendedInstallPathAndPcClientSettings extends InstallPathAndPcClientSettingsResponse {
  buildId?: string | null;
}

interface DlcSettingFormProps {
  installationPathAndPcClientSettings: InstallPathAndPcClientSettingsResponse | undefined;
  releaseType: string;
  verifyStatus: string;
  planStatus: string;
  gameId: string;
  typeGame: string;
}

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const IS_CLOSE_LAUNCH_SETTING_GUIDE = 'isClosedLaunchSettingDLCGuide';
const productNo = route.params.productId as string;

const props = defineProps<DlcSettingFormProps>();

const forceRenderKey = ref<number>(0);
const selectedTab = ref<RunOptionStatusType>(RUN_OPTION_STATUS.TEST);
const isCallClearRunOptionsApi = ref<boolean>(false);

// Store tab-specific installation settings
const testTabSettings = ref<ExtendedInstallPathAndPcClientSettings | undefined>(undefined);
const comingSoonTabSettings = ref<ExtendedInstallPathAndPcClientSettings | undefined>(undefined);
const liveTabSettings = ref<ExtendedInstallPathAndPcClientSettings | undefined>(undefined);

// Build details for Coming Soon and Live tabs
const comingSoonDetails = ref<BuildsPlanDetailResponse | undefined>(undefined);
const liveDetails = ref<BuildsPlanDetailResponse | undefined>(undefined);
const dateSettings = ref<string>('');

// Computed properties to check if settings exist for each tab
const hasTestSettings = computed(() => testTabSettings.value !== undefined);
const hasValidComingSoonSettings = computed(() =>
  comingSoonTabSettings.value !== undefined &&
  comingSoonDetails.value !== undefined &&
  comingSoonTabSettings.value.buildId !== undefined &&
  comingSoonTabSettings.value.buildId !== null
);
const hasLiveSettings = computed(() =>
  liveTabSettings.value !== undefined &&
  liveDetails.value !== undefined &&
  liveTabSettings.value.buildId !== undefined &&
  liveTabSettings.value.buildId !== null
);

// Flag to determine if tabs should be shown
const isShowTabList = computed(
  () => hasValidComingSoonSettings.value || hasLiveSettings.value || isProductInReleaseOrReviewStatus(props.releaseType, props.verifyStatus, props.planStatus)
);

const isMounted = ref<boolean>(false);

const initTabSettings = async () => {
  if (!props.gameId) { return; }

  try {
    // Always initialize TEST tab settings
    testTabSettings.value = await fetchInstallPathAndPcClientSettingsOnTabApi(props.gameId, RUN_OPTION_STATUS.TEST) as ExtendedInstallPathAndPcClientSettings;

    // Check for COMING_SOON settings
    comingSoonTabSettings.value = await fetchInstallPathAndPcClientSettingsOnTabApi(props.gameId, RUN_OPTION_STATUS.COMING_SOON) as ExtendedInstallPathAndPcClientSettings;

    if (comingSoonTabSettings.value && comingSoonTabSettings.value.buildId) {
      // Fetch build details for COMING_SOON
      comingSoonDetails.value = await fetchBuildsPlanDetailApi({
        productNo: Number(productNo),
        buildId: comingSoonTabSettings.value.buildId
      });
    }

    // Check for LIVE settings
    liveTabSettings.value = await fetchInstallPathAndPcClientSettingsOnTabApi(props.gameId, RUN_OPTION_STATUS.LIVE) as ExtendedInstallPathAndPcClientSettings;

    if (liveTabSettings.value && liveTabSettings.value.buildId) {
      // Fetch build details for LIVE
      liveDetails.value = await fetchBuildsPlanDetailApi({
        productNo: Number(productNo),
        buildId: liveTabSettings.value.buildId
      });
    }

    // Force re-render
    forceRenderKey.value = Date.now();

    // Set mounted state to true after all checks are done
    nextTick(() => {
      isMounted.value = true;
    });
  } catch (e) {}
};

const initializeSelectedTab = () => {
  // Check URL query parameter first
  const tabParam = router.currentRoute.value.query.tab as string;

  if (tabParam === RUN_OPTION_STATUS.LIVE && hasLiveSettings.value) {
    selectedTab.value = RUN_OPTION_STATUS.LIVE;
    return;
  }

  if (tabParam === RUN_OPTION_STATUS.COMING_SOON && hasValidComingSoonSettings.value) {
    selectedTab.value = RUN_OPTION_STATUS.COMING_SOON;
    return;
  }

  if (hasLiveSettings.value) {
    selectedTab.value = RUN_OPTION_STATUS.LIVE;
  } else if (hasValidComingSoonSettings.value) {
    selectedTab.value = RUN_OPTION_STATUS.COMING_SOON;
  } else if (hasTestSettings.value) {
    selectedTab.value = RUN_OPTION_STATUS.TEST;
  }
};

const onLoadLiveSettings = async () => {
  isCallClearRunOptionsApi.value = true;

  const result = await showConfirm({
    severity: 'info',
    content: t('studio.execution_setting.fetch_live_data_confirm_popup'),
    confirmLabel: t('studio.execution_setting.fetch_live_data_cf_btn'),
    cancelLabel: t('studio.common.popup_case_cancel_btn'),
    cancelVariant: 'outline',
    panelClasses: 'w-[39.2rem]'
  });

  if (result) {
    // Only copy from LIVE settings
    if (liveTabSettings.value) {
      // Copy settings to TEST tab with required modifications
      const testSettings = JSON.parse(JSON.stringify(liveTabSettings.value));
      // Remove id and build_id, set status to TEST as per requirements
      delete testSettings.id;
      delete testSettings.buildId;
      testSettings.status = RUN_OPTION_STATUS.TEST;

      // Update testTabSettings
      testTabSettings.value = testSettings;

      // Force re-render
      forceRenderKey.value = Date.now();
    }
  } else {
    isCallClearRunOptionsApi.value = false;
  }
};

const onCancelComingSoon = async () => {
  const result = await showConfirm({
    severity: 'info',
    content: t('studio.execution_setting.scheduled_pack_cancel_confirm_popup'),
    confirmLabel: t('studio.common.popup_case_cf_btn'),
    cancelLabel: t('studio.common.popup_case_close_btn'),
    cancelVariant: 'outline',
    panelClasses: 'w-[39.2rem]'
  });

  if (result) {
    const success = await deleteBuildScheduleApi(props.gameId, String(comingSoonTabSettings.value?.buildId));
    if (success) {
      window.location.reload();
    }
  }
};

const init = () => {
  initializeSelectedTab();
  initTabSettings();
};

// Add method to reset isCallClearRunOptionsApi
const setIsCallClearRunOptionsApi = (value: boolean) => {
  isCallClearRunOptionsApi.value = value;
};

// Add to defineExpose
defineExpose({
  setIsCallClearRunOptionsApi
});

init();
</script>
